import React, { useEffect, useState } from "react"
import { Modal } from "reactstrap"
import { AvForm } from "availity-reactstrap-validation"
import { DateRangePicker } from "react-date-range"
import { subDays } from "date-fns"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"

const DIRECTION_OPTIONS = [
  { value: "", label: "All call types" },
  { value: "incoming", label: "Incoming" },
  { value: "outgoing", label: "Outgoing" },
  { value: "missed", label: "Missed" },
  { value: "not_picked", label: "Not picked" },
]

const CALL_GAP_OPTIONS = [
  { value: "", label: "Any (no gap filter)" },
  { value: "7", label: "7+ days since last call" },
  { value: "10", label: "10+ days since last call" },
  { value: "15", label: "15+ days since last call" },
  { value: "20", label: "20+ days since last call" },
  { value: "30", label: "30+ days since last call" },
  { value: "45", label: "45+ days since last call" },
]

const CallLogReportFilter = ({
  open,
  onClose,
  dateFilter,
  setDateFilter,
  direction,
  setDirection,
  staffUserId,
  setStaffUserId,
  callGapDays,
  setCallGapDays,
  onlyLeads,
  setOnlyLeads,
  minDuration,
  setMinDuration,
  maxDuration,
  setMaxDuration,
  staffList,
  onApply,
}) => {
  const [localDirection, setLocalDirection] = useState(direction || "")
  const [localStaffId, setLocalStaffId] = useState(staffUserId || "")
  const [localGapDays, setLocalGapDays] = useState(callGapDays || "")
  const [localOnlyLeads, setLocalOnlyLeads] = useState(!!onlyLeads)
  const [localMinDuration, setLocalMinDuration] = useState(minDuration || "")
  const [localMaxDuration, setLocalMaxDuration] = useState(maxDuration || "")
  const minDate = subDays(new Date(), 45)

  useEffect(() => {
    if (open) {
      setLocalDirection(direction || "")
      setLocalStaffId(staffUserId || "")
      setLocalGapDays(callGapDays || "")
      setLocalOnlyLeads(!!onlyLeads)
      setLocalMinDuration(minDuration || "")
      setLocalMaxDuration(maxDuration || "")
    }
  }, [open, direction, staffUserId, callGapDays, onlyLeads, minDuration, maxDuration])

  const handleDateChange = item => {
    let { startDate, endDate, key } = item.selection
    endDate = endDate || startDate
    const minAllowed = subDays(new Date(), 45)
    if (startDate < minAllowed) {
      startDate = minAllowed
    }
    const maxSpanStart = subDays(endDate, 45)
    if (startDate < maxSpanStart) {
      startDate = maxSpanStart
    }
    setDateFilter([{ startDate, endDate, key: key || "selection" }])
  }

  return (
    <Modal isOpen={open} centered style={{ maxWidth: "620px" }}>
      <AvForm
        onValidSubmit={() => {
          setDirection(localDirection)
          setStaffUserId(localStaffId)
          setCallGapDays(localGapDays)
          setOnlyLeads(localOnlyLeads)
          const normalizeDuration = val =>
            val !== "" && Number(val) >= 0
              ? String(Math.floor(Number(val)))
              : ""
          setMinDuration(normalizeDuration(localMinDuration))
          setMaxDuration(normalizeDuration(localMaxDuration))
          onApply()
        }}
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0">Filter Call Logs</h5>
          <button
            type="button"
            className="cross__btn"
            onClick={onClose}
            aria-hidden="true"
          >
            &times;
          </button>
        </div>
        <div className="modal-body">
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Staff name</label>
              <select
                className="form-select"
                value={localStaffId}
                onChange={e => setLocalStaffId(e.target.value)}
              >
                <option value="">All staff</option>
                {staffList.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Call type</label>
              <select
                className="form-select"
                value={localDirection}
                onChange={e => setLocalDirection(e.target.value)}
              >
                {DIRECTION_OPTIONS.map(opt => (
                  <option key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row g-2 mb-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label fw-semibold mb-1">
                Duration (sec)
              </label>
              <div className="d-flex align-items-center gap-1">
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min="0"
                  step="1"
                  placeholder="Min"
                  value={localMinDuration}
                  onChange={e => setLocalMinDuration(e.target.value)}
                />
                <span className="text-muted">–</span>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min="0"
                  step="1"
                  placeholder="Max"
                  value={localMaxDuration}
                  onChange={e => setLocalMaxDuration(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-5">
              <label className="form-label fw-semibold mb-1">
                Call gap (max 45 days)
              </label>
              <select
                className="form-select form-select-sm"
                value={localGapDays}
                onChange={e => setLocalGapDays(e.target.value)}
              >
                {CALL_GAP_OPTIONS.map(opt => (
                  <option key={opt.value || "any"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <div className="form-check mb-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="onlyLeadsCheck"
                  checked={localOnlyLeads}
                  onChange={e => setLocalOnlyLeads(e.target.checked)}
                />
                <label
                  className="form-check-label fw-semibold"
                  htmlFor="onlyLeadsCheck"
                >
                  Only lead calls
                </label>
              </div>
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label fw-semibold mb-1">
              Date range{" "}
              <span className="text-muted fw-normal">(max last 45 days)</span>
            </label>
          </div>
          <DateRangePicker
            onChange={handleDateChange}
            showSelectionPreview
            moveRangeOnFirstSelection={false}
            months={1}
            ranges={dateFilter}
            direction="horizontal"
            minDate={minDate}
            maxDate={new Date()}
          />
        </div>
        <div className="modal-footer">
          <button type="submit" className="btn btn-primary">
            Apply Filter
          </button>
        </div>
      </AvForm>
    </Modal>
  )
}

export default CallLogReportFilter
